import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useTheme,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Folder, Business } from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { DataTable } from '../../components/DataTable/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { sekurantenProjekteAPI } from '../../services/api';
import { SekurantenProjekt } from '../../types/api';
import { useSnackbar } from 'notistack';

const columns = [
  { id: 'name', label: 'Name' },
  { id: 'description', label: 'Beschreibung' },
  {
    id: 'project_name',
    label: 'Verknüpftes Projekt',
    format: (value: string | undefined) => {
      if (!value) {
        return <Chip label="Kein Projekt" size="small" variant="outlined" />;
      }
      return (
        <Chip 
          icon={<Business />} 
          label={value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      );
    },
  },
  {
    id: 'sekuranten_count',
    label: 'Anzahl Sekuranten',
    format: (value: number) => (
      <Chip 
        icon={<Folder />} 
        label={`${value || 0} Dokumente`} 
        size="small" 
        color={value > 0 ? 'success' : 'default'}
      />
    ),
  },
  {
    id: 'created_at',
    label: 'Erstellt am',
    format: (value: string) =>
      format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: de }),
  },
];

export const SekurantenList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [projekte, setProjekte] = useState<SekurantenProjekt[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProjekt, setSelectedProjekt] = useState<SekurantenProjekt | null>(null);

  // Rollen-basierte Berechtigungen für Sekuranten
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure haben eingeschränkte Rechte
  const canSeeRestricted = isForeman || isInstaller;
  
  // Sekuranten-Berechtigungen:
  const canCreateSekuranten = canSeeAll || canSeeRestricted; // Alle können erstellen
  const canEditSekuranten = canSeeAll; // Nur Admin, Management, Bauleiter können alle bearbeiten
  const canDeleteSekuranten = canSeeAll; // Nur Admin, Management, Bauleiter können löschen

  const fetchProjekte = async () => {
    try {
      setLoading(true);
      const response = await sekurantenProjekteAPI.getProjekte();
      setProjekte(response);
    } catch (error) {
      console.error('Error fetching sekuranten projekte:', error);
      enqueueSnackbar('Fehler beim Laden der Sekuranten-Projekte', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjekte();
  }, []);

  const handleCreate = () => {
    navigate('/sekuranten/new-projekt');
  };

  const handleEdit = (projekt: SekurantenProjekt) => {
    navigate(`/sekuranten-projekte/${projekt.id}/edit`);
  };

  const handleView = (projekt: SekurantenProjekt) => {
    navigate(`/sekuranten-projekte/${projekt.id}`);
  };

  const handleDelete = async () => {
    if (!selectedProjekt) return;

    try {
      await sekurantenProjekteAPI.deleteProjekt(selectedProjekt.id);
      enqueueSnackbar('Sekuranten-Projekt erfolgreich gelöscht', { variant: 'success' });
      fetchProjekte();
    } catch (error: any) {
      console.error('Error deleting projekt:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Löschen des Projekts';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedProjekt(null);
    }
  };

  const confirmDelete = (projekt: SekurantenProjekt) => {
    setSelectedProjekt(projekt);
    setDeleteDialogOpen(true);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          Sekuranten-Projekte
        </Typography>
        {canCreateSekuranten && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Neues Sekuranten-Projekt
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <DataTable
            columns={columns}
            rows={projekte}
            loading={loading}
            initialSortBy="created_at"
            initialOrder="desc"
            onView={handleView}
            {...(canEditSekuranten && { onEdit: handleEdit })}
            {...(canDeleteSekuranten && { onDelete: confirmDelete })}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Sekuranten-Projekt löschen"
        message={
          selectedProjekt?.sekuranten_count && selectedProjekt.sekuranten_count > 0
            ? `Dieses Projekt enthält ${selectedProjekt.sekuranten_count} Sekuranten-Dokumente. Bitte löschen Sie zuerst alle Dokumente.`
            : "Sind Sie sicher, dass Sie dieses Sekuranten-Projekt löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
        }
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedProjekt(null);
        }}
        severity="error"
        confirmDisabled={selectedProjekt?.sekuranten_count ? selectedProjekt.sekuranten_count > 0 : false}
      />
    </Box>
  );
}; 